// frontend/src/pages/admin/ManageContactSubmissionsPage.tsx
import { useState, useEffect, useCallback, useMemo } from "react"; // Added useMemo
import { Link, useNavigate } from 'react-router-dom'; // Added Link and useNavigate
import {
  getContactSubmissions,
  updateContactSubmissionStatus,
  deleteContactSubmission,
  type GetContactSubmissionsParams,
} from "@/api/contactService";
import {
  type ContactResponse,
  type ContactStatus,
  contactStatuses,
} from "@/types";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'; // Added DropdownMenu components
import { Input } from '@/components/ui/input'; // Added Input for search
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from '@/components/ui/card'; // Added Card
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    MessageSquareText, // Icon for this page
    MoreHorizontal,
    Search,
    Edit3,
    Trash2,
    Loader2,
    FilterX,
    LayoutDashboard, // For Dashboard link
    FileWarning, // For error display
    ChevronDown,
    ChevronUp,
    AlertCircle
} from "lucide-react";
import { format } from 'date-fns';
import { ScrollArea } from "@/components/ui/scroll-area"

const ITEMS_PER_PAGE = 10;
const ALL_STATUSES_KEY = "ALL_STATUSES_FILTER_KEY";

const ManageContactSubmissionsPage = () => {
  const [allFetchedSubmissions, setAllFetchedSubmissions] = useState<ContactResponse[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState<number | null>(null); // For delete/update operations
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<ContactStatus | "">("");
  const [editingSubmission, setEditingSubmission] = useState<ContactResponse | null>(null);
  const [newStatus, setNewStatus] = useState<ContactStatus | "">("");
  const navigate = useNavigate();

  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const params: GetContactSubmissionsParams = {
      skip: (currentPage - 1) * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
      status: filterStatus || undefined,
    };
    try {
      const data = await getContactSubmissions(params);
      setAllFetchedSubmissions(data);
    } catch (err: any) {
        const errorMessage = err.response?.data?.detail || (err instanceof Error ? err.message : "Failed to fetch submissions");
        setError(errorMessage);
        toast.error(errorMessage);
        if (err.response && err.response.status === 401) {
            localStorage.removeItem('authToken');
            navigate('/login?sessionExpired=true');
        }
        setAllFetchedSubmissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, filterStatus, navigate]);

  useEffect(() => {
    // Debounce fetching while searching is not implemented here, direct fetch on filter/page change
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Client-side search logic (similar to ManageProductsPage)
  const filteredSubmissions = useMemo(() => {
    if (!searchTerm) {
      return allFetchedSubmissions;
    }
    return allFetchedSubmissions.filter(submission =>
      submission.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      submission.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (submission.subject && submission.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
      submission.message.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allFetchedSubmissions, searchTerm]);


  const handleStatusFilterChange = (selectedValue: string) => {
    if (selectedValue === ALL_STATUSES_KEY) {
      setFilterStatus("");
    } else {
      setFilterStatus(selectedValue as ContactStatus);
    }
    setCurrentPage(1);
  };
  
  const handleClearFilters = () => {
    setFilterStatus("");
    setSearchTerm(""); // Clear search term as well
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    setCurrentPage(newPage);
  };

  const handleOpenEditModal = (submission: ContactResponse) => {
    setEditingSubmission(submission);
    setNewStatus(submission.status);
  };

  const handleCloseEditModal = () => {
    setEditingSubmission(null);
    setNewStatus("");
  };

  const handleUpdateStatus = async () => {
    if (!editingSubmission || !newStatus) return;
    setIsProcessing(editingSubmission.id);
    try {
      await updateContactSubmissionStatus(editingSubmission.id, { status: newStatus as ContactStatus });
      toast.success(`Submission #${editingSubmission.id} status updated to ${newStatus.replace("_", " ")}`);
      handleCloseEditModal();
      fetchSubmissions(); 
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update status";
      toast.error(errorMessage);
    } finally {
        setIsProcessing(null);
    }
  };

  const handleDeleteSubmission = async (submissionId: number) => {
    setIsProcessing(submissionId);
    try {
      await deleteContactSubmission(submissionId);
      toast.success(`Submission #${submissionId} deleted successfully.`);
      fetchSubmissions(); 
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete submission.";
      toast.error(errorMessage);
    } finally {
        setIsProcessing(null);
    }
  };
  
  const getStatusBadgeStyle = (status: ContactStatus): string => {
    switch (status) {
      case "UNREVIEWED": return "bg-gray-500 hover:bg-gray-600";
      case "REVIEWED": return "bg-blue-500 hover:bg-blue-600";
      case "FOLLOWED_UP": return "bg-yellow-500 hover:bg-yellow-600 text-black";
      case "COMPLETED": return "bg-green-500 hover:bg-green-600";
      case "SPAM": return "bg-red-500 hover:bg-red-600";
      default: return "bg-gray-400 hover:bg-gray-500";
    }
  };

  const displayFilterValue = filterStatus === "" ? ALL_STATUSES_KEY : filterStatus;
  const canGoNext = allFetchedSubmissions.length === ITEMS_PER_PAGE; // Basic next check
  const canGoPrev = currentPage > 1;

  if (isLoading && allFetchedSubmissions.length === 0 && !searchTerm) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-lg text-muted-foreground">Loading submissions...</p>
      </div>
    );
  }

  if (error && allFetchedSubmissions.length === 0 && !searchTerm) {
    return (
      <div className="container mx-auto px-4 py-20 text-center min-h-[60vh] flex flex-col justify-center items-center">
        <FileWarning className="h-16 w-16 text-destructive mx-auto mb-6" />
        <h2 className="text-2xl font-semibold text-destructive mb-2">Error Loading Submissions</h2>
        <p className="text-muted-foreground mb-6">{error}</p>
        <Button onClick={fetchSubmissions} variant="outline">Try Again</Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 space-y-8 text-foreground bg-background">
      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-border/60 pb-6 relative">
        <div className="absolute bottom-0 left-0 w-1/3 h-px bg-gradient-to-r from-primary/70 via-accent/50 to-transparent"></div>
        <div className="flex items-center">
          <MessageSquareText className="h-10 w-10 text-primary mr-4 flex-shrink-0" />
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Manage <span className="text-gradient-maroon-to-gold">Submissions</span>
            </h1>
            <p className="mt-1 text-muted-foreground">
              View, manage, and update user contact submissions.
            </p>
          </div>
        </div>
        <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10 hover:text-primary transition-colors self-start sm:self-center w-full sm:w-auto">
            <Link to="/admin/dashboard">
                <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </Link>
        </Button>
      </header>

      <div className="flex flex-col sm:flex-row items-center gap-4">
        <div className="relative w-full sm:max-w-sm">
          <Input
            type="search"
            placeholder="Search by name, email, subject, message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-border focus:border-primary focus:ring-primary"
          />
          <Search className="absolute left-3 top-1/2 h-5 w-5 text-muted-foreground -translate-y-1/2" />
        </div>
        <Select value={displayFilterValue} onValueChange={handleStatusFilterChange}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES_KEY}>All Statuses</SelectItem>
            {contactStatuses.map((status) => (
              <SelectItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace("_", " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(filterStatus || searchTerm) && (
           <Button variant="outline" onClick={handleClearFilters} className="text-sm w-full sm:w-auto">
             <FilterX className="mr-2 h-4 w-4" /> Clear Filters
           </Button>
        )}
      </div>
      
      {error && !isLoading && filteredSubmissions.length > 0 && ( // Inline error if data is already present
         <div className="p-3 bg-destructive/10 text-destructive border border-destructive/30 rounded-md flex items-center">
           <AlertCircle className="h-5 w-5 mr-2" /> {error}
         </div>
       )}

      <Card className="shadow-lg bg-card border border-border/30 pt-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-vedasMaroon-800"> {/* Using a more standard muted background */}
              <TableRow className="border-border/50">
                <TableHead className="py-4 text-primary-foreground">ID</TableHead>
                <TableHead className="text-primary-foreground">Name</TableHead>
                <TableHead className="text-primary-foreground">Email & Phone</TableHead>
                <TableHead className="text-primary-foreground">Subject</TableHead>
                <TableHead className="min-w-[150px] text-primary-foreground">Status</TableHead>
                <TableHead className="text-primary-foreground">Received</TableHead>
                <TableHead className="text-right text-primary-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && filteredSubmissions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16 text-muted-foreground">
                    <div className="flex flex-col items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
                        Loading submissions...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredSubmissions.length > 0 ? (
                filteredSubmissions.map((submission) => (
                  <TableRow key={submission.id} className="border-border/40 hover:bg-muted/30 dark:hover:bg-muted/10">
                    <TableCell>{submission.id}</TableCell>
                    <TableCell className="font-medium text-foreground">{submission.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                        <div>{submission.email}</div>
                        {submission.phone && <div className="text-xs">{submission.phone}</div>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                        {submission.subject ? (submission.subject.length > 40 ? submission.subject.substring(0, 40) + '...' : submission.subject) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusBadgeStyle(submission.status)} text-white capitalize`}>
                        {submission.status.replace("_", " ")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{format(new Date(submission.created_at), "PPpp")}</TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger 
                          className="justify-self-end"
                        asChild>
                            {isProcessing === submission.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <MoreHorizontal className="h-4 w-4 cursor-pointer" />
                            )}
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-popover text-popover-foreground border-border">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleOpenEditModal(submission)} className="cursor-pointer">
                            <Edit3 className="mr-2 h-4 w-4 text-primary/80" /> View/Edit Status
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-border/50"/>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <DropdownMenuItem onSelect={(e) => e.preventDefault()} 
                                    className="text-destructive focus:text-destructive-foreground focus:bg-destructive/90 cursor-pointer"
                                    disabled={isProcessing === submission.id}>
                                    <Trash2 className="mr-2 h-4 w-4" /> Delete
                                </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This action cannot be undone. This will permanently delete submission #{submission.id} by {submission.name}.
                                </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteSubmission(submission.id)}>
                                    Confirm Delete
                                </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16 text-muted-foreground">
                    No submissions found.
                    {searchTerm && " Try adjusting your search term."}
                    {filterStatus && " Try a different status filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {!isLoading && (filteredSubmissions.length > 0 || currentPage > 1) && ( // Show pagination if there's data or if on a page > 1 (to allow going back)
        <div className="mt-6 flex justify-between items-center">
            <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!canGoPrev || isLoading || isProcessing !== null}
            >
            <ChevronUp className="mr-2 h-4 w-4 md:hidden" />
            <span className="hidden md:inline">Previous</span>
            </Button>
            <span className="text-sm text-muted-foreground">Page {currentPage}</span>
            <Button
            variant="outline"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!canGoNext || isLoading || isProcessing !== null}
            >
            <span className="hidden md:inline">Next</span>
            <ChevronDown className="ml-2 h-4 w-4 md:hidden" />
            </Button>
        </div>
      )}

      {editingSubmission && (
        <AlertDialog open={!!editingSubmission} onOpenChange={(open) => !open && handleCloseEditModal()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>View/Edit Status for Submission #{editingSubmission.id}</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="py-2 text-sm space-y-1">
                  <p><strong>Name:</strong> {editingSubmission.name}</p>
                  <p><strong>Email:</strong> {editingSubmission.email}</p>
                  {editingSubmission.phone && <p><strong>Phone:</strong> {editingSubmission.phone}</p>}
                  <p><strong>Subject:</strong> {editingSubmission.subject || "N/A"}</p>
                  <p className="mt-2"><strong>Message:</strong></p>
                  <ScrollArea className="h-32 whitespace-pre-wrap p-2 border rounded-md bg-muted"> {/* Added ScrollArea */}
                    {editingSubmission.message}
                  </ScrollArea>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <label htmlFor="status-select" className="text-right col-span-1">Status</label>
                <Select value={newStatus as string} onValueChange={(value) => setNewStatus(value as ContactStatus)}>
                  <SelectTrigger className="col-span-3">
                    <SelectValue placeholder="Select new status" />
                  </SelectTrigger>
                  <SelectContent>
                    {contactStatuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={handleCloseEditModal}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleUpdateStatus} 
                disabled={!newStatus || newStatus === editingSubmission.status || isProcessing === editingSubmission.id}
              >
                {isProcessing === editingSubmission.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin"/> : null}
                Save Status
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};

export default ManageContactSubmissionsPage;